/**
 * The Anagram Solver: which words a rack of letters can spell.
 *
 * The word list is ENABLE, a public-domain word-game list, built into
 * `public/data/enable-words.txt` by `scripts/build-word-list.mjs` (lowercase, 2–15
 * letters, one per line). It is fetched on the first search and kept for the life of
 * the page; nothing about the rack ever leaves the browser.
 *
 * Solving is a single pass over the list. A word is spellable when, letter by letter,
 * the rack still holds that letter — or, failing that, a blank (`?`) is left to stand in
 * for it. About 170,000 words and a 26-slot count per word make that a few milliseconds,
 * which is why there is no index beyond the list itself.
 */

export const WORD_LIST_URL = "/data/enable-words.txt";
export const MAX_TILES = 15;
export const MIN_WORD_LENGTH = 2;

const LETTER_A = 97;
const ALPHABET = 26;
const RE_SEPARATORS = /[\s,]+/g;
const RE_RACK = /^[a-z?]*$/;
const RE_LINE_BREAK = /\r?\n/;

/** The letters of a word in alphabetical order: "listen" and "silent" share "eilnst". */
export function anagramSignature(word: string): string {
  return Array.from(word.toLowerCase()).sort().join("");
}

export interface Rack {
  /** How many of each letter, a–z. */
  counts: Uint8Array;
  blanks: number;
  /** Letters plus blanks. */
  size: number;
  /** The rack as typed, cleaned: lowercase letters and `?`. */
  text: string;
}

export type RackResult =
  | { ok: true; rack: Rack }
  | { ok: false; reason: "empty" | "invalid" | "too-long" };

/** Read a rack: letters and `?` blanks; spaces and commas ignored, case ignored. */
export function parseRack(input: string): RackResult {
  const text = input.toLowerCase().replace(RE_SEPARATORS, "");
  if (!text) {
    return { ok: false, reason: "empty" };
  }
  if (!RE_RACK.test(text)) {
    return { ok: false, reason: "invalid" };
  }
  if (text.length > MAX_TILES) {
    return { ok: false, reason: "too-long" };
  }
  const counts = new Uint8Array(ALPHABET);
  let blanks = 0;
  for (const char of text) {
    if (char === "?") {
      blanks += 1;
    } else {
      counts[char.charCodeAt(0) - LETTER_A] += 1;
    }
  }
  return { ok: true, rack: { counts, blanks, size: text.length, text } };
}

/**
 * Can the rack spell `word`? Returns the positions a blank fills (empty when the
 * letters alone suffice), or null when it cannot. A repeated letter the rack holds
 * only once takes the blank at its later position: "?" + "a" spells "aa" as a + blank.
 */
export function spell(
  word: string,
  rack: Rack,
  scratch: Uint8Array = new Uint8Array(ALPHABET)
): number[] | null {
  scratch.set(rack.counts);
  let blanks = rack.blanks;
  const filled: number[] = [];
  for (let index = 0; index < word.length; index += 1) {
    const slot = word.charCodeAt(index) - LETTER_A;
    if (slot >= 0 && slot < ALPHABET && scratch[slot] > 0) {
      scratch[slot] -= 1;
    } else if (blanks > 0) {
      blanks -= 1;
      filled.push(index);
    } else {
      return null;
    }
  }
  return filled;
}

export interface AnagramWord {
  word: string;
  /** Positions spelled with a blank. */
  blanks: number[];
}

export interface LengthGroup {
  length: number;
  words: AnagramWord[];
}

export interface AnagramResult {
  /** Words that use every tile on the rack. */
  exact: AnagramWord[];
  /** Shorter words, longest group first, each group alphabetical. */
  shorter: LengthGroup[];
  total: number;
}

/**
 * Every word the rack spells with at least `minLength` letters: the full-rack anagrams
 * apart, the rest grouped by length (longest first) and alphabetical within a group,
 * which the sorted word list gives for free.
 */
export function solveAnagrams(
  words: readonly string[],
  rack: Rack,
  minLength = MIN_WORD_LENGTH
): AnagramResult {
  const scratch = new Uint8Array(ALPHABET);
  const exact: AnagramWord[] = [];
  const byLength = new Map<number, AnagramWord[]>();
  for (const word of words) {
    const length = word.length;
    if (length > rack.size || length < minLength) {
      continue;
    }
    const blanks = spell(word, rack, scratch);
    if (!blanks) {
      continue;
    }
    if (length === rack.size) {
      exact.push({ word, blanks });
      continue;
    }
    const group = byLength.get(length) ?? [];
    group.push({ word, blanks });
    byLength.set(length, group);
  }
  const shorter = [...byLength.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([length, group]) => ({ length, words: group }));
  const total =
    exact.length + shorter.reduce((sum, group) => sum + group.words.length, 0);
  return { exact, shorter, total };
}

/** The asset's text → its words. Blank lines (the trailing newline) are dropped. */
export function parseWordList(text: string): string[] {
  return text.split(RE_LINE_BREAK).filter((word) => word.length > 0);
}

let wordList: Promise<string[]> | null = null;

/**
 * The word list, fetched once per page load and kept in memory. A failed fetch is
 * forgotten, so the next search tries again.
 */
export function loadWordList(): Promise<string[]> {
  if (!wordList) {
    wordList = fetch(WORD_LIST_URL)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Word list returned ${response.status}`);
        }
        return response.text();
      })
      .then(parseWordList)
      .catch((error: unknown) => {
        wordList = null;
        throw error;
      });
  }
  return wordList;
}
